// ============================================
// Theme Hook - Light + Dark mode
// Persists preference to localStorage and applies
// data-theme attribute on the <html> element.
// Dark mode added 2026-05-14 per Claude Design handoff.
// ============================================

import { useState, useEffect, useCallback } from 'react';

const THEME_STORAGE_KEY = 'prtcl-theme';

// Legacy theme ids (aura-luxe / nocturnal-pulse / default) are retained so
// any previously stored value migrates cleanly on read - they are no
// longer selectable from the picker.
export type ThemeId = 'default' | 'aura-luxe' | 'nocturnal-pulse' | 'default-2' | 'dark';

export type ThemeOption = {
  id: ThemeId;
  label: string;
  description: string;
  disabled?: boolean;
};

export const THEMES: ThemeOption[] = [
  { id: 'default-2', label: 'Light', description: 'Bright and airy' },
  { id: 'dark', label: 'Dark', description: 'Easier on the eyes at night' },
];

const SELECTABLE_THEME_IDS: ReadonlyArray<ThemeId> = ['default-2', 'dark'];

function getStoredTheme(): ThemeId {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && (SELECTABLE_THEME_IDS as ReadonlyArray<string>).includes(stored)) {
      return stored as ThemeId;
    }
  } catch {
    // localStorage unavailable - fall through to default
  }
  return 'default-2';
}

export function applyTheme(theme: ThemeId): void {
  const root = document.documentElement;
  if (theme === 'default') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
}

export function initializeTheme(): void {
  applyTheme(getStoredTheme());
}

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeId>(getStoredTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // localStorage unavailable - graceful degradation
    }
  }, [theme]);

  const setTheme = useCallback((newTheme: ThemeId) => {
    setThemeState(newTheme);
  }, []);

  const isAuraLuxe = theme === 'aura-luxe';
  const isNocturnalPulse = theme === 'nocturnal-pulse';
  const isDark = theme === 'dark';

  return { theme, setTheme, isAuraLuxe, isNocturnalPulse, isDark };
}
